"use client";

import {
    Box,
    LoadingOverlay,
    Tabs,
    Stack,
    Alert,
} from "@mantine/core";
import { BaseContainer, ConfigTabs, ConfigTabItem } from "../common";
import {
    Sparkles,
    Palette,
    Settings2,
    Terminal,
    Braces,
    Columns2,
    Eye,
    ShieldCheck
} from "lucide-react";
import { notifications } from "@mantine/notifications";
import { AiReportChat } from "@/components/ai/AiReportChat";
import { ReportGeneralConfig, ReportQueryConfig, ReportVariablesConfig, ReportColumnsConfig, ReportPreview } from "@/components/reports";
import { ColumnFormatting } from "@/components/reports/ColumnFormatting";
import { RoleSelector } from "@/components/common/RoleSelector";
import { UseFormReturnType } from "@mantine/form";
import type {
    ReportUpdate,
    DataSource,
    TableSchema,
    ReportQueryResult
} from "@bi-studio/shared/lib";

export interface ReportEditViewProps {
    isSaving: boolean;
    activeTab: string | null;
    setActiveTab: (tab: string | null) => void;
    form: UseFormReturnType<ReportUpdate>;
    dataSources: DataSource[];
    schema: TableSchema[];
    error: string | null;
    errorToFix: string | null;
    setErrorToFix: (error: string | null) => void;
    handlePreview: () => void;
    isPreviewing: boolean;
    previewResult: ReportQueryResult | null;
}

export function ReportEditView({
    isSaving,
    activeTab,
    setActiveTab,
    form,
    dataSources,
    schema,
    error,
    errorToFix,
    setErrorToFix,
    handlePreview,
    isPreviewing,
    previewResult,
}: ReportEditViewProps) {
    const tabs: ConfigTabItem[] = [
        { value: "general", label: "General", icon: <Settings2 size={14} /> },
        { value: "ai", label: "AI Assistant", icon: <Sparkles size={14} /> },
        { value: "query", label: "Query", icon: <Terminal size={14} /> },
        { value: "variables", label: "Variables", icon: <Braces size={14} /> },
        { value: "columns", label: "Columns", icon: <Columns2 size={14} /> },
        { value: "formatting", label: "Formatting", icon: <Palette size={14} /> },
        { value: "permissions", label: "Permissions", icon: <ShieldCheck size={14} /> },
        { value: "preview", label: "Preview", icon: <Eye size={14} /> },
    ];

    return (
        <BaseContainer>
            <LoadingOverlay visible={isSaving} overlayProps={{ blur: 2 }} />

            <Box p={{ base: 'xs', sm: 'md' }}>
                <ConfigTabs
                    value={activeTab}
                    onChange={setActiveTab}
                    tabs={tabs}
                >
                    <Tabs.Panel value="general">
                        <ReportGeneralConfig
                            form={form}
                            isSaving={isSaving}
                            dataSources={dataSources}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="ai">
                        <Stack gap="md">
                            <Alert icon={<Sparkles size={16} />} title="AI Query Assistant" color="violet" variant="light" radius="md">
                                Describe the data you need in plain English, and the AI will generate or fix the SQL query for you. You can also ask it to refine an existing query.
                            </Alert>
                            <AiReportChat
                                dataSourceId={form.values.dataSourceId || undefined}
                                initialQuery={form.values.query || ""}
                                errorToFix={errorToFix}
                                onQueryApply={(newQuery) => {
                                    form.setFieldValue("query", newQuery);
                                    setActiveTab("query");
                                    setErrorToFix(null);
                                    notifications.show({
                                        title: "Query Updated",
                                        message: "The SQL query has been updated from the AI suggestion.",
                                        color: "green",
                                    });
                                }}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="permissions">
                        <RoleSelector
                            value={form.values.allowedRoles || []}
                            onChange={(roles) => form.setFieldValue("allowedRoles", roles)}
                            disabled={isSaving}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="query">
                        <ReportQueryConfig
                            form={form}
                            isSaving={isSaving}
                            schema={schema}
                            error={error}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="variables">
                        <ReportVariablesConfig form={form} />
                    </Tabs.Panel>

                    <Tabs.Panel value="columns">
                        <ReportColumnsConfig form={form} />
                    </Tabs.Panel>

                    <Tabs.Panel value="formatting">
                        <ColumnFormatting
                            columns={form.values.columns || []}
                            onChange={(cols) => form.setFieldValue("columns", cols)}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="preview">
                        <ReportPreview
                            form={form}
                            onPreview={handlePreview}
                            isPreviewing={isPreviewing}
                            previewResult={previewResult}
                            error={error}
                            onTroubleshoot={(err) => {
                                setErrorToFix(err);
                                setActiveTab("ai");
                            }}
                            isSaving={isSaving}
                        />
                    </Tabs.Panel>
                </ConfigTabs>
            </Box>
        </BaseContainer>
    );
}
